import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle, Clock, EnvelopeSimple, IdentificationCard, MapPin, Pause, Play, SignIn, Spinner, UserCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError, useAuth } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CorrectAnswerDirection from "../components/CorrectAnswerDirection";
import LiveProctoringMonitor from "../components/LiveProctoringMonitor";
import { livePollDelayMs, acceptPolledState } from "../lib/livePolling";
import AdmissionStatus from "../components/AdmissionStatus";
import { useAdmissionPost, admissionCancelled } from "../lib/admission";

function LiveBrand() {
  return (
    <div className="font-display text-2xl font-black tracking-tighter">
      SAKI<span className="text-primary">/</span>X<span className="text-[0.7em]">m</span>
      <span className="ml-3 text-xs font-mono-data font-normal uppercase tracking-widest text-muted-foreground">ao vivo</span>
    </div>
  );
}

function ParticipantQuestionMedia({ id, code, token, compact = false }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    api.get(`/live/${code}/media/${id}`, {
      responseType: "blob",
      headers: { "X-Live-Participant-Token": token },
    }).then((response) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(response.data);
      setUrl(objectUrl);
    }).catch(() => {});
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, code, token]);
  if (!url) return null;
  return <img src={url} alt="Ilustração da questão" className={`${compact ? "max-h-36" : "max-h-72"} max-w-full object-contain mx-auto my-4`} />;
}

function Distribution({ items = [] }) {
  return (
    <div className="space-y-2 mt-5">
      {items.map((item) => (
        <div key={item.letter} className="grid grid-cols-[28px_1fr_58px] gap-2 items-center text-xs font-mono-data">
          <b>{item.letter}</b>
          <div className="h-4 border border-border"><div className="h-full bg-primary" style={{ width: `${item.percentage}%` }} /></div>
          <span className="text-right">{item.percentage}%</span>
        </div>
      ))}
    </div>
  );
}

function remainingSeconds(deadline) {
  if (!deadline) return null;
  return Math.max(0, Math.ceil((new Date(deadline).getTime() - Date.now()) / 1000));
}

function elapsedSeconds(startedAt) {
  if (!startedAt) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
}

function formatClock(value) {
  if (value == null) return "—";
  const seconds = Math.max(0, Number(value) || 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}` : `${minutes}:${String(rest).padStart(2, "0")}`;
}

function OnlineExamExperience({ state, code, token, tokenConfig, setState, poll, answer, answering }) {
  const [proctorReady, setProctorReady] = useState(!(
    state?.proctoring_enabled || state?.single_screen_required || state?.location_required
  ));
  const [monitorStatus, setMonitorStatus] = useState({
    cameraReady: !state?.proctoring_enabled,
    screenGuardReady: !state?.single_screen_required,
    screenCount: state?.single_screen_required ? null : 1,
    selectedScreenLabel: null,
    locationReady: !state?.location_required,
  });
  const [selectedLetter, setSelectedLetter] = useState("");
  const [starting, setStarting] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [returningFromPause, setReturningFromPause] = useState(false);
  const [confirmBlank, setConfirmBlank] = useState(false);
  const [tick, setTick] = useState(0);
  const timeoutSent = useRef("");
  const resumeId = useRef("");
  const pauseResumeId = useRef("");
  const advanceRequest = useRef(false);
  const integrityAdvance = useRef("");

  const monitorReady = useCallback((ready, details = {}) => {
    setProctorReady(Boolean(ready));
    setMonitorStatus((current) => ({ ...current, ...details }));
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setSelectedLetter("");
    setConfirmBlank(false);
  }, [state?.question?.position]);

  const totalRemaining = remainingSeconds(state?.attempt_deadline_at);
  const questionRemaining = remainingSeconds(state?.question_deadline_at);

  const advance = useCallback(async (
    reason = "skip", expectedPosition = state?.current_position, quietError = false,
  ) => {
    if (advanceRequest.current || state?.attempt_status !== "in_progress" || state?.voluntary_pause?.active) return false;
    advanceRequest.current = true;
    setAdvancing(true);
    try {
      const response = await api.post(`/live/${code}/advance`, {
        reason,
        question_position: Number(expectedPosition || 0) || null,
      }, tokenConfig());
      setState(response.data);
      if (reason === "skip") toast.success("Questão encerrada sem resposta. Não será possível retornar.");
      if (reason === "integrity_violation") toast.warning("A questão foi encerrada por saída da tela fiscalizada. Não será possível retornar.");
      return true;
    } catch (error) {
      if (!quietError) toast.error(fmtError(error));
      await poll(quietError);
      return false;
    } finally {
      advanceRequest.current = false;
      setAdvancing(false);
    }
  }, [code, poll, setState, state?.attempt_status, state?.current_position, state?.voluntary_pause?.active, tokenConfig]);

  const handleIntegrityViolation = useCallback(({ questionPosition }) => {
    const position = Number(questionPosition || state?.current_position || 0);
    if (!position || state?.attempt_status !== "in_progress" || state?.voluntary_pause?.active) return;
    const key = String(position);
    if (integrityAdvance.current === key) return;
    integrityAdvance.current = key;
    setConfirmBlank(false);
    void (async () => {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (await advance("integrity_violation", position, true)) return;
        await new Promise((resolve) => window.setTimeout(resolve, 500 * (attempt + 1)));
      }
      integrityAdvance.current = "";
      toast.error("Não foi possível confirmar o avanço por saída da tela. A prova continua bloqueada; tente restaurar a tela inteira.");
    })();
  }, [advance, state?.attempt_status, state?.current_position, state?.voluntary_pause?.active]);

  const ensureFullscreen = useCallback(async () => {
    if (document.fullscreenElement) return true;
    if (!document.fullscreenEnabled || !document.documentElement.requestFullscreen) {
      toast.error("Este navegador não permite iniciar a prova em tela inteira. Use Chrome ou Edge atualizado.");
      return false;
    }
    try {
      await document.documentElement.requestFullscreen();
      return Boolean(document.fullscreenElement);
    } catch (_) {
      toast.error("Autorize a tela inteira para iniciar ou continuar a prova.");
      return false;
    }
  }, []);

  useEffect(() => {
    if (state?.attempt_status !== "in_progress" || questionRemaining !== 0 || !state?.question_deadline_at) return;
    const key = `${state.current_position}:${state.question_deadline_at}`;
    if (timeoutSent.current === key) return;
    timeoutSent.current = key;
    advance("timeout");
  }, [advance, questionRemaining, state?.attempt_status, state?.current_position, state?.question_deadline_at, tick]);

  const start = async () => {
    setStarting(true);
    try {
      if (!document.fullscreenElement) {
        if (state?.single_screen_required) {
          toast.error("Abra a prova na tela escolhida em modo de tela inteira antes de iniciar.");
          return;
        }
        if (!await ensureFullscreen()) return;
      }
      const response = await api.post(`/live/${code}/attempt/start`, {
        proctoring_consent: Boolean(state?.proctoring_enabled),
        camera_ready: Boolean(monitorStatus.cameraReady),
        screen_guard_ready: Boolean(monitorStatus.screenGuardReady),
        screen_count: monitorStatus.screenCount,
        selected_screen_label: monitorStatus.selectedScreenLabel,
        location_ready: Boolean(monitorStatus.locationReady),
      }, tokenConfig());
      setState(response.data);
      toast.success("Prova iniciada. Após avançar, não será possível retornar.");
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setStarting(false);
    }
  };

  const resume = async () => {
    if (resuming || !state?.reconnect_required || !proctorReady) return;
    setResuming(true);
    resumeId.current ||= window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      if (!document.fullscreenElement) {
        if (state?.single_screen_required) {
          toast.error("Restaure a tela escolhida em modo de tela inteira antes de continuar.");
          return;
        }
        if (!await ensureFullscreen()) return;
      }
      const response = await api.post(`/live/${code}/attempt/resume`, {
        resume_id: resumeId.current,
        camera_ready: Boolean(monitorStatus.cameraReady),
        screen_guard_ready: Boolean(monitorStatus.screenGuardReady),
        screen_count: monitorStatus.screenCount,
        selected_screen_label: monitorStatus.selectedScreenLabel,
        location_ready: Boolean(monitorStatus.locationReady),
      }, tokenConfig());
      setState(response.data);
      resumeId.current = "";
      toast.success("Conexão retomada na questão seguinte à última registrada.");
    } catch (error) {
      // Keep the same resume_id. If the first response was lost, a deliberate
      // second click is idempotent and cannot consume another return.
      toast.error(fmtError(error));
    } finally {
      setResuming(false);
    }
  };

  const returnFromPause = async () => {
    if (returningFromPause || !state?.voluntary_pause?.active || !proctorReady) return;
    setReturningFromPause(true);
    pauseResumeId.current ||= window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      if (!document.fullscreenElement) {
        if (state?.single_screen_required) {
          toast.error("Restaure a tela escolhida em modo de tela inteira antes de continuar.");
          return;
        }
        if (!await ensureFullscreen()) return;
      }
      const response = await api.post(`/live/${code}/attempt/return-from-pause`, {
        resume_id: pauseResumeId.current,
        camera_ready: Boolean(monitorStatus.cameraReady),
        screen_guard_ready: Boolean(monitorStatus.screenGuardReady),
        screen_count: monitorStatus.screenCount,
        selected_screen_label: monitorStatus.selectedScreenLabel,
        location_ready: Boolean(monitorStatus.locationReady),
      }, tokenConfig());
      setState(response.data);
      pauseResumeId.current = "";
      const pausedSeconds = Number(response.data?.voluntary_pause?.last_seconds || 0);
      toast.success(`Prova retomada após ${formatClock(pausedSeconds)} de pausa.`);
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setReturningFromPause(false);
    }
  };

  const question = state?.question;
  const inProgress = state?.attempt_status === "in_progress";
  const pauseActive = Boolean(state?.voluntary_pause?.active);
  const currentPauseSeconds = pauseActive
    ? elapsedSeconds(state?.voluntary_pause?.started_at)
    : Number(state?.voluntary_pause?.elapsed_seconds || 0);
  const startAllowed = state?.availability?.allowed !== false;
  const scheduledStart = state?.availability?.opens_at ? new Date(state.availability.opens_at).toLocaleString("pt-BR") : null;
  const scheduledClose = state?.availability?.closes_at ? new Date(state.availability.closes_at).toLocaleString("pt-BR") : null;
  return (
    <main className="min-h-screen bg-muted/20">
      {confirmBlank && inProgress && <div className="fixed inset-0 z-[95] bg-black/70 grid place-items-center p-5" role="dialog" aria-modal="true" aria-labelledby="confirm-blank-title" data-testid="confirm-blank-dialog">
        <div className="w-full max-w-md border-2 border-amber-500 bg-card p-6 shadow-2xl">
          <WarningCircle size={42} weight="fill" className="text-amber-700" />
          <h2 id="confirm-blank-title" className="font-display text-3xl font-black mt-4">Enviar esta questão em branco?</h2>
          <p className="text-sm text-muted-foreground mt-3">A questão será encerrada sem resposta e você irá para a próxima. Não será possível voltar.</p>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6">
            <Button type="button" variant="outline" onClick={() => setConfirmBlank(false)} disabled={advancing} className="rounded-none">Cancelar</Button>
            <Button type="button" onClick={() => { setConfirmBlank(false); void advance("skip", state?.current_position); }} disabled={advancing} className="rounded-none bg-amber-700 hover:bg-amber-800">{advancing ? <Spinner className="animate-spin mr-2" /> : <ArrowRight className="mr-2" />}Enviar em branco e avançar</Button>
          </div>
        </div>
      </div>}
      <header className="border-b border-border bg-card px-5 py-4 flex justify-between gap-3 items-center sticky top-0 z-20">
        <LiveBrand />
        <div className="flex gap-4 text-right font-mono-data text-xs">
          {inProgress && <><div><span className="text-muted-foreground">prova</span><br /><b>{formatClock(totalRemaining)}</b></div>{state.question_deadline_at && <div><span className="text-muted-foreground">questão</span><br /><b className={questionRemaining <= 15 ? "text-destructive" : ""}>{formatClock(questionRemaining)}</b></div>}</>}
          <div><b>{state?.participant?.display_name}</b><br />{state?.current_position || 0}/{state?.total_questions || 0}</div>
        </div>
      </header>
      <section className="max-w-5xl mx-auto p-4 md:py-8 space-y-4">
        {state?.phase !== "finished" && <LiveProctoringMonitor
          code={code} token={token} required={Boolean(state?.proctoring_enabled)}
          singleScreenRequired={Boolean(state?.single_screen_required)}
          locationRequired={Boolean(state?.location_required)}
          fullscreenRequired
          questionPosition={state?.current_position}
          active={inProgress && !pauseActive} onReadyChange={monitorReady}
          onIntegrityViolation={handleIntegrityViolation}
        />}

        {state?.phase === "lobby" && <div className="border border-border bg-card p-10 text-center"><Clock size={54} className="mx-auto text-primary" /><h1 className="font-display text-4xl font-black mt-5">{state?.availability?.reason === "not_open_yet" ? "Sua prova está programada." : state?.availability?.reason === "start_window_closed" ? "O horário para iniciar terminou." : "A prova ainda não foi aberta."}</h1><p className="text-muted-foreground mt-3">{state?.availability?.reason === "not_open_yet" ? `Você poderá iniciar a partir de ${scheduledStart}. Permaneça nesta tela; a liberação será automática.` : state?.availability?.reason === "start_window_closed" ? "Procure o professor ou responsável pela aplicação." : "Permaneça nesta tela e aguarde o professor."}</p>{scheduledClose && <div className="inline-block border border-border px-4 py-3 mt-5 text-sm"><span className="label-eyebrow">início permitido até</span><b className="block mt-1">{scheduledClose}</b></div>}</div>}

        {state?.reconnect_required && <div className="border border-amber-600 bg-card p-7 md:p-10 text-center">
          <WarningCircle size={58} weight="fill" className="text-amber-700 mx-auto" />
          <h1 className="font-display font-black text-4xl mt-5">Retome sua prova.</h1>
          <p className="text-muted-foreground mt-3">As respostas confirmadas estão salvas. Você voltará à questão seguinte à última registrada; o tempo total da prova continuou correndo.</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-6 text-left">
            <div className="border p-4"><div className="label-eyebrow">prazo para retorno</div><b>até 15 minutos</b></div>
            <div className="border p-4"><div className="label-eyebrow">retornos disponíveis</div><b>{state.reconnect_policy?.returns_remaining ?? 0} de {state.reconnect_policy?.max_returns ?? 3}</b></div>
          </div>
          <Button onClick={resume} disabled={resuming || !proctorReady} className="rounded-none h-12 mt-6 font-mono-data uppercase tracking-widest">
            {resuming ? <Spinner className="animate-spin mr-2" /> : <ArrowRight className="mr-2" />} Retomar prova
          </Button>
        </div>}

        {pauseActive && !state?.reconnect_required && <div className="border-2 border-primary bg-card p-7 md:p-10 text-center" data-testid="voluntary-pause-screen">
          <Pause size={58} weight="fill" className="text-primary mx-auto" />
          <div className="label-eyebrow mt-5">pausa voluntária</div>
          <h1 className="font-display font-black text-4xl mt-2">Sua resposta foi salva.</h1>
          <p className="text-muted-foreground mt-3">A questão {state?.voluntary_pause?.after_question_position || "anterior"} foi encerrada e não poderá ser reaberta. Ao retomar, a prova começará na questão {state?.voluntary_pause?.next_question_position || state?.current_position}.</p>
          <div className="grid sm:grid-cols-3 gap-3 mt-7 text-left">
            <div className="border border-primary bg-primary/5 p-4"><div className="label-eyebrow">tempo nesta pausa</div><b className="font-mono-data text-2xl">{formatClock(currentPauseSeconds)}</b></div>
            <div className="border p-4"><div className="label-eyebrow">pausas realizadas</div><b className="font-mono-data text-2xl">{state?.voluntary_pause?.count || 0}</b><div className="text-xs text-muted-foreground mt-1">{formatClock(state?.voluntary_pause?.total_seconds || currentPauseSeconds)} fora no total</div></div>
            <div className="border p-4"><div className="label-eyebrow">tempo restante da prova</div><b className="font-mono-data text-2xl">{formatClock(totalRemaining)}</b></div>
          </div>
          <div className="border border-amber-500 bg-amber-50 p-4 mt-5 text-sm text-left"><b>O tempo total da prova continua correndo durante a pausa.</b> O cronômetro da próxima questão só começa quando você retomar. Você pode pausar novamente depois de enviar outra resposta.</div>
          <Button onClick={returnFromPause} disabled={returningFromPause || !proctorReady} className="rounded-none h-12 mt-6 font-mono-data uppercase tracking-widest">
            {returningFromPause ? <Spinner className="animate-spin mr-2" /> : <Play weight="fill" className="mr-2" />} Retomar na próxima questão
          </Button>
          {!proctorReady && <p className="text-xs text-destructive mt-3">Reative as exigências da prova nos controles acima para liberar a retomada.</p>}
        </div>}

        {state?.phase === "ready" && <div className="border border-border bg-card p-7 md:p-10">
          <div className="label-eyebrow">prova online · tentativa individual</div>
          <h1 className="font-display text-4xl font-black mt-2">{state.exam_name}</h1>
          <div className="grid sm:grid-cols-3 gap-3 mt-7">
            <div className="border p-4"><div className="label-eyebrow">questões</div><b className="text-2xl">{state.total_questions}</b></div>
            <div className="border p-4"><div className="label-eyebrow">tempo total</div><b className="text-2xl">{Number(state.total_time_minutes || 0) + Number(state.global_extra_time_minutes || 0) + Number(state.extra_time_minutes || 0)} min</b>{(state.global_extra_time_minutes || state.extra_time_minutes) ? <div className="text-xs text-primary mt-1">inclui tempo extra</div> : null}</div>
            <div className="border p-4"><div className="label-eyebrow">por questão</div><b className="text-2xl">{state.per_question_time_seconds ? `${state.per_question_time_seconds}s` : "sem limite"}</b></div>
          </div>
          {!startAllowed && <div className="border border-destructive bg-destructive/5 p-4 mt-5 text-sm text-destructive"><b>{state?.availability?.reason === "start_window_closed" ? "O horário para iniciar esta prova terminou." : "A prova ainda não está liberada para início."}</b>{scheduledClose && <span className="block mt-1">Horário-limite: {scheduledClose}</span>}</div>}
          <div className="border border-amber-500 bg-amber-50 p-4 mt-5 text-sm text-left">
            <b>Regras obrigatórias:</b>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Cada questão será encerrada ao responder, avançar ou terminar o tempo. Não será possível voltar.</li>
              <li>Depois de confirmar uma resposta, você poderá avançar ou pausar. As pausas são ilimitadas, mas o tempo total da prova continua correndo.</li>
              {state?.proctoring_enabled && <li>A câmera deve permanecer ativa. A tela inteira também será autorizada para enviar uma captura somente quando houver uma ocorrência.</li>}
              <li>Se você sair da tela da prova, a questão aberta será encerrada imediatamente e não poderá ser retomada.</li>
              {state?.single_screen_required && <li>Se outro monitor for ligado ou a tela fiscalizada for trocada, o sistema fará uma captura da tela, avançará a questão e bloqueará a prova até a regularização.</li>}
            </ul>
          </div>
          <Button onClick={start} disabled={starting || !proctorReady || !startAllowed} className="rounded-none h-12 mt-6 font-mono-data uppercase tracking-widest">
            {starting ? <Spinner className="animate-spin mr-2" /> : <ArrowRight className="mr-2" />} Entrar em tela inteira e iniciar
          </Button>
        </div>}

        {state?.phase === "finished" && <div className="border border-emerald-600 bg-card p-10 text-center">
          <CheckCircle size={58} weight="fill" className="text-emerald-600 mx-auto" />
          <h1 className="font-display font-black text-4xl mt-5">Prova finalizada.</h1>
          <div className="grid grid-cols-3 gap-3 mt-8">
            <div className="border p-4"><div className="label-eyebrow">acertos</div><div className="font-display text-3xl font-black">{state.summary?.hits || 0}</div></div>
            <div className="border p-4"><div className="label-eyebrow">respondidas</div><div className="font-display text-3xl font-black">{state.summary?.answered || 0}</div></div>
            <div className="border p-4"><div className="label-eyebrow">nota</div><div className="font-display text-3xl font-black">{state.summary?.score || 0}%</div></div>
          </div>
          <p className="text-sm text-muted-foreground mt-6">O resultado e a linha do tempo de ocorrências foram registrados. Toda ocorrência depende de revisão humana.</p>
        </div>}

        {question && inProgress && !pauseActive && !state?.reconnect_required && <div className="border border-border bg-card">
          <div className="border-b border-border p-5 flex justify-between gap-3"><div className="label-eyebrow">questão {question.position} de {state.total_questions}</div><div className="text-xs font-mono-data text-primary">sem retorno</div></div>
          <div className="p-5 md:p-8">
            <h1 className="text-xl md:text-2xl font-semibold leading-relaxed">{question.stem}</h1>
            {(question.stem_media_ids || []).map((id) => <ParticipantQuestionMedia key={id} id={id} code={code} token={token} />)}
            <div className="space-y-3 mt-7">{(question.alternatives || []).map((option) => {
              const selected = selectedLetter === option.letter;
              return <button type="button" key={option.letter} disabled={answering || advancing} onClick={() => setSelectedLetter(option.letter)} aria-pressed={selected} className={`w-full border p-4 text-left active:scale-[0.99] disabled:opacity-60 ${selected ? "border-primary bg-primary/10 ring-2 ring-primary/20" : "border-border hover:border-primary"}`}><b className="mr-2 text-primary">{option.letter}</b> {option.text}{option.media_id && <ParticipantQuestionMedia id={option.media_id} code={code} token={token} compact />}</button>;
            })}</div>
            <div className="mt-6 border border-border bg-muted/30 p-4 text-sm">
              {selectedLetter ? <>Alternativa <b className="text-primary">{selectedLetter}</b> selecionada. Ela só será enviada quando você confirmar.</> : "Selecione uma alternativa; nada será enviado automaticamente."}
            </div>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-5">
              <Button variant="outline" disabled={answering || advancing} onClick={() => setConfirmBlank(true)} className="rounded-none">Enviar em branco</Button>
              {Number(question.position) < Number(state.total_questions || 0) && <Button variant="outline" disabled={!selectedLetter || answering || advancing} onClick={() => answer(selectedLetter, { pauseAfter: true })} className="rounded-none border-primary text-primary hover:bg-primary/10">{answering ? <Spinner className="animate-spin mr-2" /> : <Pause weight="fill" className="mr-2" />}Confirmar resposta e pausar</Button>}
              <Button disabled={!selectedLetter || answering || advancing} onClick={() => answer(selectedLetter, { pauseAfter: false })} className="rounded-none">{answering ? <Spinner className="animate-spin mr-2" /> : <CheckCircle className="mr-2" />}Confirmar resposta e avançar <ArrowRight className="ml-2" /></Button>
            </div>
          </div>
        </div>}
      </section>
    </main>
  );
}

export default function LiveParticipantPage() {
  const admission = useAdmissionPost();
  const { code = "" } = useParams();
  const normalizedCode = code.toUpperCase();
  const { user } = useAuth();
  const storageKey = `sakix-live-${normalizedCode}`;
  const [summary, setSummary] = useState(null);
  const [state, setState] = useState(null);
  const [token, setToken] = useState(() => window.localStorage.getItem(storageKey) || "");
  const [guestName, setGuestName] = useState("");
  const [guestCpf, setGuestCpf] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [locating, setLocating] = useState(false);
  const [answering, setAnswering] = useState(false);
  const polling = useRef(false);
  const latestState = useRef(state);
  latestState.current = state;

  const tokenConfig = useCallback(() => ({ headers: { "X-Live-Participant-Token": token } }), [token]);

  const poll = useCallback(async (quiet = true) => {
    if (!token || polling.current) return;
    polling.current = true;
    try {
      const config = tokenConfig();
      const current = latestState.current;
      if (current?.state_revision != null) config.params = {
        revision: current.state_revision,
        ...(current.attempt_revision != null ? { attempt_revision: current.attempt_revision } : {}),
      };
      const response = await api.get(`/live/${normalizedCode}/state`, config);
      if (!response.data?.unchanged) setState((previous) => acceptPolledState(previous, response.data));
    } catch (error) {
      if (error?.response?.status === 403) {
        window.localStorage.removeItem(storageKey);
        setToken("");
        setState(null);
      }
      if (!quiet) toast.error(fmtError(error));
    } finally {
      polling.current = false;
      setLoading(false);
    }
  }, [normalizedCode, storageKey, token, tokenConfig]);

  useEffect(() => {
    api.get(`/live/${normalizedCode}`)
      .then((response) => setSummary(response.data))
      .catch((error) => toast.error(fmtError(error)))
      .finally(() => !token && setLoading(false));
  }, [normalizedCode, token]);

  useEffect(() => { if (token) poll(false); }, [token, poll]);

  useEffect(() => {
    if (!token || state?.phase === "finished") return undefined;
    let cancelled = false;
    let timer;
    const schedule = () => {
      timer = window.setTimeout(async () => {
        await poll();
        if (!cancelled) schedule();
      }, livePollDelayMs(state?.session_mode || summary?.session_mode));
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState !== "hidden") poll();
    };
    schedule();
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("online", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("online", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [token, state?.phase, state?.session_mode, summary?.session_mode, poll]);

  const join = async () => {
    setJoining(true);
    try {
      let locationPayload = {};
      if (summary?.location_required) {
        if (!navigator.geolocation) throw new Error("Este dispositivo não oferece localização para registrar o local da prova.");
        setLocating(true);
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, () => reject(new Error("Não foi possível registrar sua localização. Autorize o acesso e tente novamente.")), {
            enableHighAccuracy: true, timeout: 20000, maximumAge: 30000,
          });
        });
        locationPayload = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy_m: position.coords.accuracy,
        };
      }
      const response = await admission.post(`/live/${normalizedCode}/join`, {
        display_name: guestName || null,
        cpf: guestCpf || null,
        email: guestEmail || null,
        ...locationPayload,
      });
      const value = response.data.participant_token;
      window.localStorage.setItem(storageKey, value);
      setToken(value);
      toast.success(`Você entrou como ${response.data.participant.display_name}`);
    } catch (error) {
      if (!admissionCancelled(error)) toast.error(error?.response ? fmtError(error) : (error?.message || "Não foi possível entrar na sala"));
    } finally {
      setLocating(false);
      setJoining(false);
    }
  };

  const answer = async (letter, { pauseAfter = false } = {}) => {
    if (!state?.question || state.answer_locked) return;
    setAnswering(true);
    try {
      const response = await api.post(`/live/${normalizedCode}/answer`, {
        question_position: state.question.position,
        selected_letter: letter,
        pause_after: Boolean(pauseAfter),
      }, tokenConfig());
      setState(response.data);
      toast.success(pauseAfter && response.data?.voluntary_pause?.active
        ? "Resposta registrada. Pausa iniciada."
        : "Resposta registrada");
    } catch (error) {
      toast.error(fmtError(error));
      await poll();
    } finally {
      setAnswering(false);
    }
  };

  if (loading || user === undefined) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner size={30} className="animate-spin text-primary" /></div>;
  }

  if (!token) {
    const registeredOnly = summary?.audience_mode === "registered_only";
    const loggedStudent = Boolean(user?.student_id);
    return (
      <main className="min-h-screen bg-muted/20 flex items-center justify-center p-5">
        <section className="w-full max-w-lg border border-border bg-card p-7 md:p-10">
          <LiveBrand />
          <div className="label-eyebrow mt-12">código {normalizedCode}</div>
          <h1 className="font-display font-black text-4xl mt-2">{summary?.exam_name || "Prova ao vivo"}</h1>
          <div className="mt-5"><AdmissionStatus queue={admission.queue} onCancel={admission.cancel} /></div>
          {summary?.session_mode === "online_exam" && summary?.availability?.mode === "scheduled_window" && (
            <div className="mt-5 border border-primary bg-primary/5 p-4 text-sm">
              <div className="label-eyebrow text-primary">janela para iniciar</div>
              <b className="block mt-2">{summary.availability.opens_at ? new Date(summary.availability.opens_at).toLocaleString("pt-BR") : "—"} até {summary.availability.closes_at ? new Date(summary.availability.closes_at).toLocaleString("pt-BR") : "—"}</b>
              {summary.availability.reason === "not_open_yet" && <span className="block text-muted-foreground mt-1">Você pode fazer o pré-acesso agora e aguardar a liberação automática.</span>}
              {summary.availability.reason === "start_window_closed" && <span className="block text-destructive mt-1">O horário para iniciar esta prova terminou.</span>}
            </div>
          )}
          {summary?.location_required && !summary?.finished && (
            <div className="mt-5 border border-amber-500 bg-amber-50 p-4 flex gap-3 text-sm">
              <MapPin size={24} weight="fill" className="text-amber-700 shrink-0" />
              <div><b>Localização obrigatória</b><div className="text-muted-foreground mt-1">Ao entrar, o navegador solicitará sua localização para registrar onde a prova foi iniciada.{summary.geofence_enabled ? ` Também é necessário estar a até ${summary.geofence_radius_km} km do local definido pelo professor.` : " Sem essa autorização, não será possível iniciar."}</div></div>
            </div>
          )}
          {summary?.session_mode === "online_exam" && !summary?.finished && (
            <div className="mt-5 border border-amber-500 bg-amber-50 p-4 flex gap-3 text-sm">
              <WarningCircle size={24} weight="fill" className="text-amber-700 shrink-0" />
              <div>
                <b>Regras antes de entrar</b>
                <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
                  {summary?.proctoring_enabled && <li>A câmera é obrigatória durante toda a prova. Você também autorizará a tela inteira para registrar uma imagem somente nas ocorrências.</li>}
                  <li>Sair da tela encerra a questão aberta e avança sem permitir retorno.</li>
                  <li>Após enviar uma resposta, você poderá pausar quantas vezes precisar. O tempo total continuará correndo e a retomada será sempre na próxima questão.</li>
                  {summary?.single_screen_required && <li>Ligar outro monitor ou trocar a tela fiscalizada registra uma captura da tela e também avança a questão.</li>}
                </ul>
              </div>
            </div>
          )}
          {summary?.finished ? (
            <div className="mt-8 border border-border p-5"><WarningCircle className="text-amber-600" size={28} /><b className="block mt-3">Esta aula já foi encerrada.</b></div>
          ) : registeredOnly && !loggedStudent ? (
            <div className="mt-8">
              <p className="text-muted-foreground">Esta sala aceita somente alunos cadastrados e vinculados à turma da prova.</p>
              <Button asChild className="rounded-none w-full mt-5 font-mono-data uppercase tracking-widest">
                <Link to={`/login?next=${encodeURIComponent(`/live/${normalizedCode}`)}`}><SignIn className="mr-2" />Entrar como aluno</Link>
              </Button>
            </div>
          ) : (
            <div className="mt-8">
              {loggedStudent ? (
                <div className="border border-primary bg-primary/5 p-4 flex gap-3 items-center"><UserCircle size={28} className="text-primary" /><div><b>{user.name}</b><div className="text-xs text-muted-foreground">Sua resposta será vinculada ao seu perfil.</div></div></div>
              ) : (
                <>
                  <Button asChild variant="outline" className="rounded-none w-full h-12 font-mono-data uppercase tracking-widest border-primary text-primary">
                    <Link to={`/login?next=${encodeURIComponent(`/live/${normalizedCode}`)}`}><SignIn className="mr-2" />Já sou aluno — entrar</Link>
                  </Button>
                  <div className="flex items-center gap-3 my-7 text-xs font-mono-data uppercase tracking-widest text-muted-foreground">
                    <div className="h-px bg-border flex-1" /> ou ainda não tenho cadastro <div className="h-px bg-border flex-1" />
                  </div>
                  <div className="border border-border p-5 space-y-4">
                    <div>
                      <div className="font-bold">Identificação provisória</div>
                      <p className="text-xs text-muted-foreground mt-1">Seus dados ficarão registrados nesta prova. Isso não cria uma conta automaticamente.</p>
                    </div>
                    <label className="block">
                      <span className="label-eyebrow">nome completo</span>
                      <Input value={guestName} onChange={(event) => setGuestName(event.target.value)} maxLength={120} className="rounded-none mt-2" placeholder="Nome completo" autoComplete="name" />
                    </label>
                    <label className="block">
                      <span className="label-eyebrow">cpf</span>
                      <div className="relative mt-2">
                        <IdentificationCard className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input value={guestCpf} onChange={(event) => setGuestCpf(event.target.value.replace(/\D/g, "").slice(0, 11))} inputMode="numeric" maxLength={11} className="rounded-none pl-10 font-mono-data" placeholder="Somente 11 números" autoComplete="off" />
                      </div>
                    </label>
                    <label className="block">
                      <span className="label-eyebrow">e-mail</span>
                      <div className="relative mt-2">
                        <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input value={guestEmail} onChange={(event) => setGuestEmail(event.target.value)} type="email" maxLength={254} className="rounded-none pl-10" placeholder="voce@email.com" autoComplete="email" />
                      </div>
                    </label>
                  </div>
                </>
              )}
              <Button onClick={join} disabled={joining || summary?.entry_open === false || (!loggedStudent && (guestName.trim().length < 2 || guestCpf.length !== 11 || !guestEmail.includes("@")))} className="rounded-none w-full h-12 mt-5 font-mono-data uppercase tracking-widest">
                {joining ? <Spinner className="animate-spin mr-2" /> : summary?.location_required ? <MapPin className="mr-2" /> : <SignIn className="mr-2" />} {locating ? "Registrando localização…" : loggedStudent ? "Entrar na sala" : "Registrar dados e participar"}
              </Button>
              {summary?.entry_open === false && <p className="text-sm text-destructive mt-3">A entrada de novos alunos nesta prova foi encerrada.</p>}
            </div>
          )}
        </section>
      </main>
    );
  }

  const question = state?.question;
  if (state?.session_mode === "online_exam") {
    return <OnlineExamExperience
      state={state} code={normalizedCode} token={token} tokenConfig={tokenConfig}
      setState={setState} poll={poll} answer={answer} answering={answering}
    />;
  }
  return (
    <main className="min-h-screen bg-muted/20">
      <header className="border-b border-border bg-card px-5 py-4 flex justify-between gap-3 items-center">
        <LiveBrand />
        <div className="font-mono-data text-xs text-right"><b>{state?.participant?.display_name}</b><br />{state?.current_position || 0}/{state?.total_questions || 0}</div>
      </header>
      <section className="max-w-3xl mx-auto p-5 md:py-10">
        {state?.phase === "lobby" && (
          <div className="border border-border bg-card p-10 text-center min-h-[420px] flex flex-col items-center justify-center">
            <Clock size={54} weight="duotone" className="text-primary" />
            <h1 className="font-display font-black text-4xl mt-6">Você entrou.</h1>
            <p className="text-muted-foreground mt-3">Aguarde o professor iniciar a primeira questão.</p>
          </div>
        )}
        {state?.phase === "finished" && (
          <div className="border border-emerald-600 bg-card p-10 text-center">
            <CheckCircle size={58} weight="fill" className="text-emerald-600 mx-auto" />
            <h1 className="font-display font-black text-4xl mt-5">Aula encerrada.</h1>
            <div className="grid grid-cols-3 gap-3 mt-8">
              <div className="border p-4"><div className="label-eyebrow">acertos</div><div className="font-display text-3xl font-black">{state.summary?.hits || 0}</div></div>
              <div className="border p-4"><div className="label-eyebrow">respondidas</div><div className="font-display text-3xl font-black">{state.summary?.answered || 0}</div></div>
              <div className="border p-4"><div className="label-eyebrow">nota</div><div className="font-display text-3xl font-black">{state.summary?.score || 0}%</div></div>
            </div>
            {state.participant?.participant_type === "registered" && <p className="text-sm text-muted-foreground mt-6">O resultado foi incluído no seu histórico.</p>}
          </div>
        )}
        {question && state?.phase !== "finished" && (
          <div className="border border-border bg-card">
            <div className="border-b border-border p-5 flex justify-between gap-3">
              <div className="label-eyebrow">questão {question.position} de {state.total_questions}</div>
              <div className={`text-xs font-mono-data font-bold ${state.phase === "revealed" ? "text-emerald-700" : "text-primary"}`}>
                {state.phase === "revealed" ? "resposta revelada" : state.my_answer ? "resposta enviada" : "selecione uma alternativa"}
              </div>
            </div>
            <div className="p-5 md:p-8">
              <h1 className="text-xl md:text-2xl font-semibold leading-relaxed">{question.stem}</h1>
              {(question.stem_media_ids || []).map((id) => <ParticipantQuestionMedia key={id} id={id} code={normalizedCode} token={token} />)}
              <div className="space-y-3 mt-7">
                {(question.alternatives || []).map((option) => {
                  const selected = state.my_answer === option.letter;
                  const correct = state.phase === "revealed" && option.is_correct;
                  const wrong = state.phase === "revealed" && selected && !option.is_correct;
                  return (
                    <button
                      type="button"
                      key={option.letter}
                      disabled={state.answer_locked || answering}
                      onClick={() => answer(option.letter)}
                      className={`w-full border p-4 text-left transition-all ${correct ? "border-emerald-600 bg-emerald-50" : wrong ? "border-destructive bg-destructive/5" : selected ? "border-primary bg-primary/5" : "border-border hover:border-primary"} ${state.answer_locked ? "cursor-default" : "active:scale-[0.99]"}`}
                    >
                      <b className="mr-2">{option.letter}</b> {option.text}
                      {option.media_id && <ParticipantQuestionMedia id={option.media_id} code={normalizedCode} token={token} compact />}
                      {correct && <CheckCircle className="inline float-right text-emerald-700" weight="fill" />}
                    </button>
                  );
                })}
              </div>
              {state.phase === "answering" && state.my_answer && (
                <div className="mt-6 border border-primary bg-primary/5 p-4 text-sm"><CheckCircle className="inline mr-2 text-primary" weight="fill" />Resposta registrada. Aguarde o professor revelar.</div>
              )}
              {state.phase === "revealed" && (
                <div className="mt-7">
                  {question.annulled ? <div className="border border-amber-500 bg-amber-50 p-4 font-bold">Questão anulada — não conta na nota.</div> : <>
                    <CorrectAnswerDirection alternatives={question.alternatives} />
                    {question.explanation && <div className="mt-3 border-l-4 border-emerald-600 bg-emerald-50 p-4"><b>Comentário:</b> {question.explanation}</div>}
                  </>}
                  <Distribution items={state.distribution} />
                  <p className="text-center text-sm text-muted-foreground mt-6">Aguarde a próxima questão.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
